import { useState, useRef, useEffect } from "react";
import { Sun, Moon, Monitor, Check } from "lucide-react";
import { useTheme, type Theme } from "../hooks/useTheme";

export function ThemeSwitcher() {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click or escape key
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const options: { value: Theme; label: string; description: string; icon: typeof Sun }[] = [
    {
      value: "light",
      label: "Light",
      description: "Crisp daylight theme",
      icon: Sun,
    },
    {
      value: "dark",
      label: "Dark",
      description: "Deep obsidian theme",
      icon: Moon,
    },
    {
      value: "system",
      label: "System",
      description: `Follows OS (${resolvedTheme === "dark" ? "Dark" : "Light"})`,
      icon: Monitor,
    },
  ];

  const CurrentIcon =
    theme === "system"
      ? Monitor
      : theme === "dark"
      ? Moon
      : Sun;

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="inline-flex items-center justify-center p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800/80 shadow-2xs transition"
        title={`Theme: ${theme.charAt(0).toUpperCase() + theme.slice(1)} (Click to switch)`}
        aria-label="Toggle color theme"
        aria-expanded={isOpen}
      >
        <CurrentIcon className="w-4 h-4 text-stone-600 dark:text-stone-300" />
      </button>

      {isOpen && (
        <div className="absolute left-0 bottom-full mb-1.5 w-48 origin-bottom-left rounded-xl bg-white dark:bg-stone-900 shadow-lg ring-1 ring-black/5 dark:ring-white/10 border border-stone-200 dark:border-stone-800 py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100">
          <div className="px-3 py-1.5 border-b border-stone-100 dark:border-stone-800/80">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-500">
              Appearance
            </p>
          </div>

          <div className="py-1">
            {options.map((opt) => {
              const isSelected = theme === opt.value;
              const Icon = opt.icon;

              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setTheme(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left transition ${
                    isSelected
                      ? "bg-stone-100/80 dark:bg-stone-800 text-stone-900 dark:text-stone-100 font-semibold"
                      : "text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800/60"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    <Icon className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400 shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate leading-tight">{opt.label}</span>
                      <span className="text-[10px] text-stone-400 dark:text-stone-500 font-normal leading-tight">
                        {opt.description}
                      </span>
                    </div>
                  </div>
                  {isSelected && (
                    <Check className="w-3.5 h-3.5 text-stone-900 dark:text-stone-100 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
